// Pooled combat effects: tracers, muzzle flashes, impacts, hit sparks, explosions, smoke clouds and
// the flashbang overlay. Nothing here allocates per frame: every pool is a fixed typed-array batch.

import * as THREE from "three";
import type { ShellKind } from "./weaponFx";
import { ParticleBatch, ParticleSpec, KIND } from "./particles";
import { TracerBatch } from "./tracers";
import { DecalBatch } from "./decals";
import { ease } from "./viewmodelMath";
import type { Viewmodel } from "./viewmodel";
import type { QualityPreset } from "./quality";
import type { EffectsRenderer, Vec3Like } from "./types";

type RGB = readonly [number, number, number];

interface ExplosionStyle {
  fireStart: RGB;
  fireEnd: RGB;
  fireRadius: number;
  fireLife: number;
  ring: RGB;
  ringRadius: number;
  ringLife: number;
  star: number;
  chips: number;
  smokes: number;
  smokeRadius: number;
  sparks: number;
  spark: RGB;
  light: number;
  lightRange: number;
  lightColor: number;
}

const EXPLOSIONS: Readonly<Record<string, ExplosionStyle>> = {
  frag: {
    fireStart: [3.2, 1.7, 0.6],
    fireEnd: [0.9, 0.18, 0.04],
    fireRadius: 3.2,
    fireLife: 0.5,
    ring: [2.2, 1.5, 0.9],
    ringRadius: 12,
    ringLife: 0.42,
    star: 0,
    chips: 14,
    smokes: 9,
    smokeRadius: 2.6,
    sparks: 18,
    spark: [2.4, 1.5, 0.6],
    light: 420,
    lightRange: 42,
    lightColor: 0xffa860,
  },
  bomb: {
    fireStart: [3.4, 1.8, 0.6],
    fireEnd: [0.9, 0.16, 0.03],
    fireRadius: 9,
    fireLife: 0.9,
    ring: [2.4, 1.4, 0.8],
    ringRadius: 34,
    ringLife: 0.8,
    star: 0,
    chips: 40,
    smokes: 26,
    smokeRadius: 6.5,
    sparks: 40,
    spark: [2.4, 1.5, 0.6],
    light: 1400,
    lightRange: 90,
    lightColor: 0xff9a50,
  },
  flash: {
    fireStart: [4, 4, 4.4],
    fireEnd: [1.2, 1.2, 1.4],
    fireRadius: 1.4,
    fireLife: 0.2,
    ring: [2.2, 2.2, 2.4],
    ringRadius: 7,
    ringLife: 0.3,
    star: 5,
    chips: 0,
    smokes: 0,
    smokeRadius: 0,
    sparks: 10,
    spark: [2.2, 2.2, 2.4],
    light: 700,
    lightRange: 50,
    lightColor: 0xffffff,
  },
  smoke: {
    fireStart: [1.1, 1.15, 1.2],
    fireEnd: [0.4, 0.42, 0.45],
    fireRadius: 0.9,
    fireLife: 0.18,
    ring: [0.6, 0.62, 0.65],
    ringRadius: 5,
    ringLife: 0.5,
    star: 0,
    chips: 0,
    smokes: 7,
    smokeRadius: 1.8,
    sparks: 4,
    spark: [1.2, 1.2, 1.2],
    light: 0,
    lightRange: 10,
    lightColor: 0xffffff,
  },
  impulse: {
    fireStart: [0.6, 2.4, 3.2],
    fireEnd: [0.1, 0.5, 1.2],
    fireRadius: 2.6,
    fireLife: 0.32,
    ring: [0.5, 2.0, 2.8],
    ringRadius: 11,
    ringLife: 0.4,
    star: 2.5,
    chips: 0,
    smokes: 0,
    smokeRadius: 0,
    sparks: 14,
    spark: [0.5, 2.0, 2.8],
    light: 300,
    lightRange: 30,
    lightColor: 0x40c0ff,
  },
};

interface Fireball {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  age: number;
  life: number;
  radius: number;
  start: THREE.Color;
  end: THREE.Color;
}

interface Cloud {
  until: number;
}

const FIREBALLS = 4;
const _n = new THREE.Vector3();
const _t1 = new THREE.Vector3();
const _t2 = new THREE.Vector3();
const _v = new THREE.Vector3();
const _w = new THREE.Vector3();

function rand(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

/** Seconds until something `height` m above the floor, rising at `vy` under `gravity`, reaches it. */
function fallTime(height: number, vy: number, gravity: number): number {
  if (height <= 0) return 0;
  return (vy + Math.sqrt(vy * vy + 2 * gravity * height)) / gravity;
}

export class Effects implements EffectsRenderer {
  /** Drawn after the whole frame (post-processing included): the flashbang white-out. */
  readonly overlayScene = new THREE.Scene();
  readonly overlayCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  private readonly additive = new ParticleBatch(640, true, "fx-additive");
  private readonly soft = new ParticleBatch(768, false, "fx-soft");
  private readonly smokeBatch = new ParticleBatch(640, false, "fx-smoke");
  private readonly vmBatch = new ParticleBatch(48, true, "fx-viewmodel", false);
  private readonly vmSoft = new ParticleBatch(48, false, "fx-viewmodel-soft", false);
  private readonly tracers = new TracerBatch(64);
  private readonly decals = new DecalBatch(160);
  private readonly spec = new ParticleSpec();
  private readonly fireballs: Fireball[] = [];
  private readonly clouds = new Map<number, Cloud>();
  private readonly overlayMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly overlayMesh: THREE.Mesh;
  private readonly overlayGeometry = new THREE.PlaneGeometry(2, 2);
  private readonly fireGeometry = new THREE.IcosahedronGeometry(1, 2);

  private readonly fxLight: THREE.PointLight;
  private readonly viewmodel: Viewmodel;
  private scale = 1;
  private time = 0;
  private frameCount = 0;
  private flashLevel = 0;
  private flashHold = 0;
  private muzzleE = 0;
  private blastE = 0;
  private blastPeak = 1;
  private blastRange = 30;
  private readonly muzzlePos = new THREE.Vector3();
  private readonly blastPos = new THREE.Vector3();
  private vmFlashE = 0;
  private muzzlePower = 1;
  private vmFlashPower = 1;
  private readonly pendingShells: { kind: ShellKind; t: number }[] = [];

  constructor(world: THREE.Scene, viewmodel: Viewmodel, fxLight: THREE.PointLight) {
    this.viewmodel = viewmodel;
    this.fxLight = fxLight;
    world.add(this.decals.mesh, this.soft.mesh, this.smokeBatch.mesh, this.additive.mesh, this.tracers.mesh);
    viewmodel.scene.add(this.vmBatch.mesh, this.vmSoft.mesh);
    this.vmBatch.mesh.renderOrder = 30;
    this.vmSoft.mesh.renderOrder = 29;
    for (let i = 0; i < FIREBALLS; i++) {
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        fog: false,
      });
      const mesh = new THREE.Mesh(this.fireGeometry, material);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.renderOrder = 18;
      world.add(mesh);
      this.fireballs.push({
        mesh,
        material,
        age: 0,
        life: 1,
        radius: 1,
        start: new THREE.Color(),
        end: new THREE.Color(),
      });
    }
    this.overlayMesh = new THREE.Mesh(this.overlayGeometry, this.overlayMaterial);
    this.overlayMesh.frustumCulled = false;
    this.overlayMesh.visible = false;
    this.overlayScene.add(this.overlayMesh);
  }

  setQuality(q: QualityPreset): void {
    this.scale = q.particleScale;
  }

  /** True while the white-out overlay has something to draw. */
  get overlayVisible(): boolean {
    return this.overlayMesh.visible;
  }

  // ---- tracers / muzzle -------------------------------------------------------------------

  tracer(from: Vec3Like, to: Vec3Like, local: boolean): void {
    this.tracers.add(from.x, from.y, from.z, to.x, to.y, to.z, local);
  }

  muzzleFlash(pos: Vec3Like, dir: Vec3Like, local: boolean, power = 1): void {
    if (local) {
      this.muzzlePos.set(pos.x + dir.x * 0.5, pos.y + dir.y * 0.5, pos.z + dir.z * 0.5);
      this.muzzleE = 1;
      this.muzzlePower = power;
      if (this.viewmodel.active && this.viewmodel.muzzleScenePosition(_v)) {
        this.viewmodelFlash(_v.x, _v.y, _v.z, power);
        this.vmFlashE = 1;
        this.vmFlashPower = power;
        this.viewmodel.flashLight.position.set(_v.x, _v.y + 0.04, _v.z - 0.12);
      }
      return;
    }
    const s = this.spec.reset();
    s.kind = KIND.STAR;
    s.x = pos.x + dir.x * 0.08;
    s.y = pos.y + dir.y * 0.08;
    s.z = pos.z + dir.z * 0.08;
    s.life = 0.06;
    s.size0 = s.size1 = rand(0.32, 0.46) * power;
    s.rot = rand(0, Math.PI);
    s.color(2.2, 1.6, 0.8);
    s.fadeIn = 0;
    s.fadeOut = 0.06;
    this.additive.spawn(s);
    if (Math.random() < this.scale) {
      const w = this.spec.reset();
      w.kind = KIND.PUFF;
      w.x = pos.x + dir.x * 0.15;
      w.y = pos.y + dir.y * 0.15;
      w.z = pos.z + dir.z * 0.15;
      w.vx = dir.x * 1.4;
      w.vy = dir.y * 1.4 + 0.25;
      w.vz = dir.z * 1.4;
      w.life = 0.55;
      w.size0 = 0.08;
      w.size1 = 0.34;
      w.color(0.75, 0.75, 0.75);
      w.alpha = 0.16;
      w.fadeIn = 0.03;
      w.fadeOut = 0.4;
      w.drag = 2;
      this.soft.spawn(w);
    }
  }

  /**
   * Flash of the first-person gun, drawn in viewmodel camera space at the muzzle: a hot four-point
   * star, a whiter star on the diagonal, an orange bloom, gas sparks thrown forward and (on every
   * other shot) a thin wisp of smoke. It lasts ~75 ms, which is several frames at any refresh rate.
   */
  private viewmodelFlash(x: number, y: number, z: number, power: number): void {
    let s = this.spec.reset();
    s.kind = KIND.STAR;
    s.x = x;
    s.y = y;
    s.z = z - 0.02;
    s.life = 0.075;
    s.size0 = s.size1 = rand(0.2, 0.26) * power;
    s.rot = rand(0, Math.PI);
    s.color(2.8, 2.0, 0.9);
    s.fadeIn = 0;
    s.fadeOut = 0.075;
    this.vmBatch.spawn(s);
    s = this.spec.reset();
    s.kind = KIND.STAR;
    s.x = x;
    s.y = y;
    s.z = z - 0.03;
    s.life = 0.05;
    s.size0 = s.size1 = rand(0.11, 0.15) * power;
    s.rot = rand(0, Math.PI) + Math.PI / 4;
    s.color(3, 2.8, 2.3);
    s.fadeIn = 0;
    s.fadeOut = 0.05;
    this.vmBatch.spawn(s);
    s = this.spec.reset();
    s.kind = KIND.PUFF;
    s.x = x;
    s.y = y;
    s.z = z - 0.04;
    s.life = 0.06;
    s.size0 = 0.2 * power;
    s.size1 = 0.34 * power;
    s.color(2.2, 1.1, 0.35);
    s.alpha = 0.55;
    s.fadeIn = 0;
    s.fadeOut = 0.06;
    this.vmBatch.spawn(s);
    const sparks = Math.round(4 * this.scale);
    for (let i = 0; i < sparks; i++) {
      s = this.spec.reset();
      s.kind = KIND.SPARK;
      s.x = x;
      s.y = y;
      s.z = z - 0.03;
      s.vx = rand(-1.4, 1.4);
      s.vy = rand(-0.6, 1.4);
      s.vz = -rand(3, 7) * power;
      s.life = rand(0.05, 0.1);
      s.size0 = 0.03;
      s.size1 = 0.012;
      s.color(2.6, 1.8, 0.8).colorTo(1.4, 0.5, 0.1);
      s.fadeIn = 0;
      s.fadeOut = s.life * 0.6;
      this.vmBatch.spawn(s);
    }
    if (Math.random() < 0.5 * this.scale) {
      s = this.spec.reset();
      s.kind = KIND.PUFF;
      s.x = x;
      s.y = y;
      s.z = z - 0.05;
      s.vx = rand(-0.05, 0.05);
      s.vy = rand(0.1, 0.3);
      s.vz = -rand(0.3, 0.8);
      s.life = 0.5;
      s.size0 = 0.03;
      s.size1 = 0.11;
      s.color(0.7, 0.7, 0.72);
      s.alpha = 0.12;
      s.fadeIn = 0.03;
      s.fadeOut = 0.4;
      s.drag = 2.5;
      this.vmSoft.spawn(s);
    }
  }

  // ---- shells -----------------------------------------------------------------------------

  ejectShell(kind: ShellKind, delaySec: number): void {
    if (kind === "none") return;
    if (delaySec > 0.01) {
      if (this.pendingShells.length < 4) this.pendingShells.push({ kind, t: delaySec });
      return;
    }
    this.spawnShell(kind);
  }

  /** One case tumbles out of the ejection port of the first-person gun and falls away. */
  private spawnShell(kind: ShellKind): void {
    if (!this.viewmodel.active || !this.viewmodel.ejectScenePose(_v, _w)) return;
    const s = this.spec.reset();
    const speed = rand(1.7, 3);
    s.kind = KIND.CHIP;
    s.x = _v.x;
    s.y = _v.y;
    s.z = _v.z;
    s.vx = _w.x * speed + rand(-0.3, 0.3);
    s.vy = _w.y * speed + rand(-0.2, 0.4);
    s.vz = _w.z * speed + rand(-0.3, 0.3);
    s.life = 0.9;
    s.size0 = s.size1 = kind === "hull" ? 0.03 : kind === "rifle" ? 0.021 : 0.016;
    s.rot = rand(0, Math.PI);
    s.spin = rand(-30, 30);
    if (kind === "hull") s.color(0.75, 0.1, 0.08);
    else s.color(0.95, 0.66, 0.2);
    s.gravity = 9;
    s.drag = 0.3;
    s.fadeIn = 0;
    s.fadeOut = 0.3;
    this.vmSoft.spawn(s);
  }

  // ---- impacts ----------------------------------------------------------------------------

  /** Fills _t1 / _t2 with two unit vectors perpendicular to the unit normal in _n. */
  private basis(): void {
    if (Math.abs(_n.y) < 0.9) _t1.set(0, 1, 0).cross(_n);
    else _t1.set(1, 0, 0).cross(_n);
    _t1.normalize();
    _t2.crossVectors(_n, _t1);
  }

  impact(pos: Vec3Like, normal: Vec3Like, eyeDist = 20): void {
    _n.set(normal.x, normal.y, normal.z);
    if (_n.lengthSq() < 1e-8) _n.set(0, 1, 0);
    _n.normalize();
    this.basis();
    // Hits right in front of the shooter are small and few: the puff sits under the crosshair.
    const near = Math.min(Math.max((eyeDist - 1.5) / 7, 0.15), 1);
    this.decals.add(pos.x, pos.y, pos.z, _n.x, _n.y, _n.z, rand(0.14, 0.18) * (0.45 + 0.55 * near));

    const puffs = near < 0.35 ? 1 : Math.round(3 * this.scale);
    for (let i = 0; i < puffs; i++) {
      const s = this.spec.reset();
      const a = rand(-0.8, 0.8);
      const b = rand(-0.8, 0.8);
      const sp = rand(1.2, 3);
      s.kind = KIND.PUFF;
      s.x = pos.x + _n.x * 0.02;
      s.y = pos.y + _n.y * 0.02;
      s.z = pos.z + _n.z * 0.02;
      s.vx = (_n.x + _t1.x * a + _t2.x * b) * sp;
      s.vy = (_n.y + _t1.y * a + _t2.y * b) * sp + 0.15;
      s.vz = (_n.z + _t1.z * a + _t2.z * b) * sp;
      s.life = rand(0.3, 0.45);
      s.size0 = 0.03 * near;
      s.size1 = rand(0.1, 0.17) * (0.3 + 0.7 * near);
      s.color(0.8, 0.78, 0.74);
      s.alpha = 0.18 + 0.14 * near;
      s.fadeIn = 0.02;
      s.fadeOut = s.life * 0.8;
      s.drag = 3.5;
      this.soft.spawn(s);
    }
    const chips = Math.round(3 * this.scale);
    for (let i = 0; i < chips; i++) {
      const s = this.spec.reset();
      const a = rand(-1, 1);
      const b = rand(-1, 1);
      const sp = rand(1.5, 4);
      s.kind = KIND.CHIP;
      s.x = pos.x + _n.x * 0.02;
      s.y = pos.y + _n.y * 0.02;
      s.z = pos.z + _n.z * 0.02;
      s.vx = (_n.x + _t1.x * a + _t2.x * b) * sp;
      s.vy = (_n.y + _t1.y * a + _t2.y * b) * sp + 0.8;
      s.vz = (_n.z + _t1.z * a + _t2.z * b) * sp;
      s.life = rand(0.4, 0.7);
      s.size0 = s.size1 = rand(0.01, 0.02);
      s.rot = rand(0, Math.PI);
      s.spin = rand(-12, 12);
      s.color(0.42, 0.4, 0.37);
      s.gravity = 11;
      s.fadeIn = 0;
      s.fadeOut = 0.15;
      this.soft.spawn(s);
    }
    const sparks = Math.round(3 * this.scale);
    for (let i = 0; i < sparks; i++) {
      const s = this.spec.reset();
      const a = rand(-0.9, 0.9);
      const b = rand(-0.9, 0.9);
      const sp = rand(2, 5);
      s.kind = KIND.SPARK;
      s.x = pos.x + _n.x * 0.02;
      s.y = pos.y + _n.y * 0.02;
      s.z = pos.z + _n.z * 0.02;
      s.vx = (_n.x + _t1.x * a + _t2.x * b) * sp;
      s.vy = (_n.y + _t1.y * a + _t2.y * b) * sp;
      s.vz = (_n.z + _t1.z * a + _t2.z * b) * sp;
      s.life = rand(0.14, 0.28);
      s.size0 = 0.05;
      s.size1 = 0.02;
      s.color(2.2, 1.6, 0.7).colorTo(1.2, 0.4, 0.1);
      s.gravity = 9;
      s.fadeIn = 0;
      s.fadeOut = s.life * 0.7;
      this.additive.spawn(s);
    }
    const f = this.spec.reset();
    f.kind = KIND.STAR;
    f.x = pos.x + _n.x * 0.04;
    f.y = pos.y + _n.y * 0.04;
    f.z = pos.z + _n.z * 0.04;
    f.life = 0.05;
    f.size0 = f.size1 = 0.16 * (0.5 + 0.5 * near);
    f.rot = rand(0, Math.PI);
    f.color(2, 1.5, 0.8);
    f.fadeIn = 0;
    f.fadeOut = 0.05;
    this.additive.spawn(f);
  }

  hitSpark(pos: Vec3Like): void {
    const n = Math.round(8 * this.scale);
    for (let i = 0; i < n; i++) {
      const s = this.spec.reset();
      _v.set(rand(-1, 1), rand(-0.4, 1), rand(-1, 1)).normalize().multiplyScalar(rand(1.5, 4.2));
      s.kind = KIND.SPARK;
      s.x = pos.x;
      s.y = pos.y;
      s.z = pos.z;
      s.vx = _v.x;
      s.vy = _v.y;
      s.vz = _v.z;
      s.life = rand(0.16, 0.32);
      s.size0 = 0.06;
      s.size1 = 0.02;
      s.color(2.4, 1.7, 0.8).colorTo(1.3, 0.45, 0.1);
      s.gravity = 6;
      s.fadeIn = 0;
      s.fadeOut = s.life * 0.7;
      this.additive.spawn(s);
    }
    const f = this.spec.reset();
    f.kind = KIND.STAR;
    f.x = pos.x;
    f.y = pos.y;
    f.z = pos.z;
    f.life = 0.07;
    f.size0 = f.size1 = 0.26;
    f.rot = rand(0, Math.PI);
    f.color(2.2, 1.7, 1);
    f.fadeIn = 0;
    f.fadeOut = 0.07;
    this.additive.spawn(f);
  }

  // ---- glass ------------------------------------------------------------------------------

  /**
   * A pane (box `min`..`max`) bursting: shards spread over the pane, thrown from the hit point `at`
   * along `push` (m/s) and falling until `floorY`, a sparkle at the hit and a puff of glass dust.
   * The bullet holes on the pane go with it.
   */
  shatter(min: Vec3Like, max: Vec3Like, at: Vec3Like, push: Vec3Like, floorY: number): void {
    this.decals.removeInside(min.x - 0.05, min.y - 0.05, min.z - 0.05, max.x + 0.05, max.y + 0.05, max.z + 0.05);
    // The two largest extents span the pane; its area sets the shard count.
    const [a, b] = [max.x - min.x, max.y - min.y, max.z - min.z].sort((p, q) => q - p);
    const area = (a ?? 0) * (b ?? 0);
    const shards = Math.round(Math.min(56, Math.max(18, area * 14)) * this.scale);
    for (let i = 0; i < shards; i++) {
      const s = this.spec.reset();
      // Shards start anywhere on the pane, a little crowded around the hit.
      const k = Math.random() < 0.35 ? 0.6 : 0;
      s.kind = KIND.SHARD;
      s.x = rand(min.x, max.x) * (1 - k) + at.x * k;
      s.y = rand(min.y, max.y) * (1 - k) + at.y * k;
      s.z = rand(min.z, max.z) * (1 - k) + at.z * k;
      const away = rand(0.3, 1.1);
      s.vx = push.x * away + rand(-0.9, 0.9) + (s.x - at.x) * 1.5;
      s.vy = push.y * away + rand(-0.4, 1.2) + (s.y - at.y) * 0.8;
      s.vz = push.z * away + rand(-0.9, 0.9) + (s.z - at.z) * 1.5;
      s.gravity = 13;
      s.drag = 0.4;
      s.life = Math.min(rand(0.9, 1.7), fallTime(s.y - floorY, s.vy, s.gravity) + 0.06);
      s.size0 = s.size1 = Math.random() < 0.2 ? rand(0.2, 0.32) : rand(0.07, 0.18);
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-16, 16);
      s.color(0.88, 0.97, 1);
      s.alpha = 0.95;
      s.fadeIn = 0;
      s.fadeOut = Math.min(0.25, s.life * 0.4);
      this.soft.spawn(s);
    }
    const sparkles = Math.round(7 * this.scale);
    for (let i = 0; i < sparkles; i++) {
      const s = this.spec.reset();
      _v.set(rand(-1, 1), rand(-0.6, 1), rand(-1, 1)).normalize().multiplyScalar(rand(1, 3.5));
      s.kind = KIND.SPARK;
      s.x = at.x;
      s.y = at.y;
      s.z = at.z;
      s.vx = _v.x + push.x * 0.5;
      s.vy = _v.y + push.y * 0.5;
      s.vz = _v.z + push.z * 0.5;
      s.life = rand(0.12, 0.3);
      s.size0 = 0.06;
      s.size1 = 0.02;
      s.color(1.8, 2, 2.2).colorTo(0.6, 0.8, 1);
      s.gravity = 8;
      s.fadeIn = 0;
      s.fadeOut = s.life * 0.7;
      this.additive.spawn(s);
    }
    for (let i = 0; i < 3; i++) {
      const s = this.spec.reset();
      s.kind = KIND.PUFF;
      s.x = at.x + rand(-0.15, 0.15);
      s.y = at.y + rand(-0.15, 0.15);
      s.z = at.z + rand(-0.15, 0.15);
      s.vx = push.x * 0.3 + rand(-0.3, 0.3);
      s.vy = push.y * 0.3 + rand(-0.1, 0.3);
      s.vz = push.z * 0.3 + rand(-0.3, 0.3);
      s.life = rand(0.5, 0.8);
      s.size0 = 0.15;
      s.size1 = rand(0.5, 0.8);
      s.color(0.85, 0.93, 0.97);
      s.alpha = 0.16;
      s.fadeOut = s.life * 0.8;
      s.drag = 2.5;
      this.soft.spawn(s);
    }
  }

  // ---- explosions -------------------------------------------------------------------------

  explosion(pos: Vec3Like, kind: string): void {
    const st = EXPLOSIONS[kind] ?? EXPLOSIONS.frag;
    const sc = this.scale;
    this.startFireball(pos, st);

    // Shock ring (camera-facing) and a soft glow.
    const ring = this.spec.reset();
    ring.kind = KIND.RING;
    ring.x = pos.x;
    ring.y = pos.y + 0.3;
    ring.z = pos.z;
    ring.life = st.ringLife;
    ring.size0 = 0.8;
    ring.size1 = st.ringRadius * 2;
    ring.grow = st.ringLife * 0.9;
    ring.color(st.ring[0], st.ring[1], st.ring[2]);
    ring.fadeIn = 0;
    ring.fadeOut = st.ringLife * 0.8;
    this.additive.spawn(ring);
    const glow = this.spec.reset();
    glow.kind = KIND.PUFF;
    glow.x = pos.x;
    glow.y = pos.y + 0.2;
    glow.z = pos.z;
    glow.life = st.fireLife * 1.2;
    glow.size0 = st.fireRadius;
    glow.size1 = st.fireRadius * 3;
    glow.color(st.fireStart[0], st.fireStart[1], st.fireStart[2]).colorTo(st.fireEnd[0], st.fireEnd[1], st.fireEnd[2]);
    glow.alpha = 0.6;
    glow.fadeIn = 0;
    glow.fadeOut = glow.life * 0.9;
    this.additive.spawn(glow);
    if (st.star > 0) {
      const star = this.spec.reset();
      star.kind = KIND.STAR;
      star.x = pos.x;
      star.y = pos.y + 0.2;
      star.z = pos.z;
      star.life = 0.25;
      star.size0 = st.star * 0.6;
      star.size1 = st.star;
      star.grow = 0.1;
      star.color(3, 3, 3.2);
      star.fadeIn = 0;
      star.fadeOut = 0.22;
      this.additive.spawn(star);
    }

    for (let i = 0, n = Math.round(st.sparks * sc); i < n; i++) {
      const s = this.spec.reset();
      _v.set(rand(-1, 1), rand(-0.2, 1), rand(-1, 1)).normalize().multiplyScalar(rand(8, 18) * (st.fireRadius / 3.2) ** 0.5);
      s.kind = KIND.SPARK;
      s.x = pos.x;
      s.y = pos.y + 0.3;
      s.z = pos.z;
      s.vx = _v.x;
      s.vy = _v.y;
      s.vz = _v.z;
      s.life = rand(0.3, 0.7);
      s.size0 = 0.1;
      s.size1 = 0.03;
      s.color(st.spark[0], st.spark[1], st.spark[2]).colorTo(st.spark[0] * 0.4, st.spark[1] * 0.2, st.spark[2] * 0.1);
      s.gravity = 12;
      s.drag = 0.8;
      s.fadeIn = 0;
      s.fadeOut = s.life * 0.6;
      this.additive.spawn(s);
    }
    for (let i = 0, n = Math.round(st.chips * sc); i < n; i++) {
      const s = this.spec.reset();
      _v.set(rand(-1, 1), rand(0.1, 1.2), rand(-1, 1)).normalize().multiplyScalar(rand(4, 12) * (st.fireRadius / 3.2) ** 0.5);
      s.kind = KIND.CHIP;
      s.x = pos.x;
      s.y = pos.y + 0.2;
      s.z = pos.z;
      s.vx = _v.x;
      s.vy = _v.y;
      s.vz = _v.z;
      s.life = rand(0.8, 1.5);
      s.size0 = s.size1 = rand(0.07, 0.16);
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-9, 9);
      s.color(0.26, 0.23, 0.2);
      s.gravity = 14;
      s.drag = 0.3;
      s.fadeIn = 0;
      s.fadeOut = 0.35;
      this.soft.spawn(s);
    }
    for (let i = 0, n = Math.round(st.smokes * sc); i < n; i++) {
      const s = this.spec.reset();
      _v.set(rand(-1, 1), rand(-0.1, 1), rand(-1, 1)).normalize().multiplyScalar(rand(0.5, 3) * (st.smokeRadius / 2.6));
      s.kind = KIND.SMOKE;
      s.x = pos.x + _v.x * 0.4;
      s.y = pos.y + 0.3 + Math.abs(_v.y) * 0.4;
      s.z = pos.z + _v.z * 0.4;
      s.vx = _v.x;
      s.vy = _v.y + rand(0.6, 1.6);
      s.vz = _v.z;
      s.life = rand(1.3, 2.2);
      s.size0 = st.smokeRadius * 0.35;
      s.size1 = st.smokeRadius * rand(0.9, 1.3);
      s.grow = s.life * 0.7;
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-0.4, 0.4);
      const dark = kind === "smoke" || kind === "flash" ? 0.8 : 0.2;
      s.color(dark, dark, dark * 1.02);
      s.alpha = kind === "smoke" ? 0.5 : 0.6;
      s.fadeIn = 0.1;
      s.fadeOut = s.life * 0.55;
      s.drag = 1.4;
      this.smokeBatch.spawn(s);
    }

    if (st.light > 0) {
      this.blastPos.set(pos.x, pos.y + 1.2, pos.z);
      this.blastE = 1;
      this.blastPeak = st.light;
      this.blastRange = st.lightRange;
      this.fxLight.color.setHex(st.lightColor);
    }
  }

  private startFireball(pos: Vec3Like, st: ExplosionStyle): void {
    let fb: Fireball | null = null;
    for (const f of this.fireballs) {
      if (!f.mesh.visible) {
        fb = f;
        break;
      }
      if (fb === null || f.age / f.life > fb.age / fb.life) fb = f;
    }
    if (fb === null) return;
    fb.age = 0;
    fb.life = st.fireLife;
    fb.radius = st.fireRadius;
    fb.start.setRGB(st.fireStart[0], st.fireStart[1], st.fireStart[2]);
    fb.end.setRGB(st.fireEnd[0], st.fireEnd[1], st.fireEnd[2]);
    fb.mesh.position.set(pos.x, pos.y + st.fireRadius * 0.25, pos.z);
    fb.mesh.rotation.set(rand(0, 6), rand(0, 6), 0);
    fb.mesh.visible = true;
  }

  // ---- smoke ------------------------------------------------------------------------------

  smoke(key: number, pos: Vec3Like, radius: number, remainingSec: number): void {
    if (remainingSec <= 0.05 || this.clouds.has(key)) return;
    this.clouds.set(key, { until: this.time + remainingSec });
    const count = Math.min(Math.max(Math.round(radius * 6 * this.scale), 10), 60);
    const fadeOut = Math.min(1.8, remainingSec * 0.5);
    for (let i = 0; i < count; i++) {
      // Random point inside a sphere of 0.62 r, kept above the floor.
      let x = 0;
      let y = 0;
      let z = 0;
      for (let attempt = 0; attempt < 4; attempt++) {
        x = rand(-1, 1);
        y = rand(-0.15, 1);
        z = rand(-1, 1);
        if (x * x + y * y + z * z <= 1) break;
      }
      const s = this.spec.reset();
      s.kind = KIND.SMOKE;
      s.x = pos.x + x * radius * 0.62;
      s.y = pos.y + 0.5 + y * radius * 0.62;
      s.z = pos.z + z * radius * 0.62;
      s.vx = rand(-0.12, 0.12);
      s.vy = rand(0, 0.08);
      s.vz = rand(-0.12, 0.12);
      s.life = remainingSec;
      s.size1 = radius * rand(0.5, 0.85);
      s.size0 = s.size1 * 0.25;
      s.grow = rand(0.7, 1.3);
      s.rot = rand(0, Math.PI * 2);
      s.spin = rand(-0.18, 0.18);
      const g = rand(0.8, 0.92);
      s.color(g, g + 0.01, g + 0.03);
      s.alpha = 0.62;
      s.fadeIn = 0.5;
      s.fadeOut = fadeOut;
      s.drag = 0.5;
      this.smokeBatch.spawn(s);
    }
  }

  // ---- flash overlay ----------------------------------------------------------------------

  flash(intensity: number): void {
    const v = Math.min(Math.max(intensity, 0), 1);
    // A weaker flash never cuts a stronger one short.
    if (v < this.flashLevel) return;
    this.flashLevel = v;
    this.flashHold = 0.35;
  }

  // ---- frame update -----------------------------------------------------------------------

  /** Called by the Renderer once per frame. */
  frame(dt: number): void {
    this.time += dt;
    this.frameCount++;
    this.additive.update(dt);
    this.soft.update(dt);
    this.smokeBatch.update(dt);
    this.vmBatch.update(dt);
    this.vmSoft.update(dt);
    for (let i = this.pendingShells.length - 1; i >= 0; i--) {
      const p = this.pendingShells[i] as { kind: ShellKind; t: number };
      p.t -= dt;
      if (p.t <= 0) {
        this.pendingShells.splice(i, 1);
        this.spawnShell(p.kind);
      }
    }
    this.tracers.update(dt);
    this.decals.update(dt);

    for (const f of this.fireballs) {
      if (!f.mesh.visible) continue;
      f.age += dt;
      const t = f.age / f.life;
      if (t >= 1) {
        f.mesh.visible = false;
        continue;
      }
      f.mesh.scale.setScalar(f.radius * (0.3 + 0.7 * ease(t * 2.2)));
      f.material.color.copy(f.start).lerp(f.end, t);
      f.material.opacity = (1 - t) * (1 - t);
    }

    // The single dynamic light follows whichever source is currently brighter.
    this.muzzleE = Math.max(0, this.muzzleE - dt / 0.06);
    this.blastE = Math.max(0, this.blastE - dt / 0.32);
    const muzzleI = 10 * this.muzzlePower * this.muzzleE * this.muzzleE;
    const blastI = this.blastPeak * this.blastE * this.blastE;
    if (blastI > muzzleI) {
      this.fxLight.intensity = blastI;
      this.fxLight.distance = this.blastRange;
      this.fxLight.position.copy(this.blastPos);
    } else {
      this.fxLight.intensity = muzzleI;
      this.fxLight.distance = 14;
      this.fxLight.position.copy(this.muzzlePos);
      if (muzzleI > 0) this.fxLight.color.setHex(0xffc880);
    }
    this.vmFlashE = Math.max(0, this.vmFlashE - dt / 0.06);
    this.viewmodel.flashLight.intensity = 3 * this.vmFlashPower * this.vmFlashE * this.vmFlashE;

    // Flash overlay: hold at the peak briefly, then fade out.
    if (this.flashLevel > 0) {
      if (this.flashHold > 0) this.flashHold -= dt;
      else this.flashLevel = Math.max(0, this.flashLevel - dt * 0.55);
    }
    this.overlayMaterial.opacity = this.flashLevel;
    this.overlayMesh.visible = this.flashLevel > 0.004;

    if (this.frameCount % 30 === 0 && this.clouds.size > 0) {
      for (const [key, c] of this.clouds) if (this.time > c.until) this.clouds.delete(key);
    }
  }

  clear(): void {
    this.additive.clear();
    this.soft.clear();
    this.smokeBatch.clear();
    this.vmBatch.clear();
    this.vmSoft.clear();
    this.pendingShells.length = 0;
    this.tracers.clear();
    this.decals.clear();
    this.clouds.clear();
    for (const f of this.fireballs) f.mesh.visible = false;
    this.flashLevel = 0;
    this.flashHold = 0;
    this.muzzleE = 0;
    this.blastE = 0;
    this.vmFlashE = 0;
    this.fxLight.intensity = 0;
    this.viewmodel.flashLight.intensity = 0;
    this.overlayMaterial.opacity = 0;
    this.overlayMesh.visible = false;
  }

  dispose(): void {
    this.clear();
    this.additive.dispose();
    this.soft.dispose();
    this.smokeBatch.dispose();
    this.vmBatch.dispose();
    this.vmSoft.dispose();
    this.tracers.dispose();
    this.decals.dispose();
    for (const f of this.fireballs) {
      f.mesh.removeFromParent();
      f.material.dispose();
    }
    this.fireGeometry.dispose();
    this.overlayMaterial.dispose();
    this.overlayGeometry.dispose();
  }
}
